import { useRef } from "react";
import { ExternalLink, Loader2, Printer, Radio } from 'lucide-react';
import type { getSceneScout } from '@generated/server/sceneScout';
import { VSR_LOGO } from '@generated/components/AdGuide';

type Scout = Awaited<ReturnType<typeof getSceneScout>>;
type Ev = Scout['events'][number];

const tone: Record<string, string> = {
  'Send a reporter': 'bg-primary text-primary-foreground',
  'Worth a photo': 'bg-[hsl(var(--brand-yellow))] text-foreground',
  'Brief / listing': 'border border-foreground',
};

function Item({ e }: { e: Ev }) {
  return (
    <li className="break-inside-avoid border-t-2 border-foreground py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${tone[e.priority] ?? 'border border-foreground'}`}>{e.priority}</span>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{e.category}</span>
      </div>
      <h4 className="mt-1.5 font-[family-name:var(--font-heading)] text-lg font-semibold leading-snug">{e.title}</h4>
      <p className="text-sm font-semibold">{e.when} · {e.venue}{e.where && e.where !== '—' ? `, ${e.where}` : ''}{e.cost ? ` · ${e.cost}` : ''}</p>
      <p className="mt-1 text-sm">{e.whyCover}</p>
      <p className="mt-1 text-sm"><strong>Angle:</strong> {e.angle}</p>
      {e.link?.startsWith('http') && <p className="mt-1 text-xs text-muted-foreground"><a href={e.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 underline underline-offset-2">Source <ExternalLink className="h-3 w-3" /></a></p>}
    </li>
  );
}

/** The Scene: AI reads the underground venue sites and drafts an assignment sheet. */
export type SceneScout = Scout;
export function SceneReport({ s, busy, error, run }: { s: Scout | null; busy: boolean; error: string | null; run: () => void }) {
  const docRef = useRef<HTMLDivElement>(null);
  const print = () => {
    const node = docRef.current; if (!node) return;
    const w = window.open('', '_blank', 'width=900,height=1000'); if (!w) { window.print(); return; }
    const styles = [...document.querySelectorAll('style, link[rel="stylesheet"]')].map((x) => x.outerHTML).join('');
    w.document.write(`<!doctype html><html class="${document.documentElement.className}"><head><title>The Scene · Village Star-Revue</title>${styles}<style>@page{margin:.5in}body{background:#fff}.no-print{display:none!important}</style></head><body>${node.outerHTML}</body></html>`);
    w.document.close(); setTimeout(() => { w.focus(); w.print(); }, 600);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={run} disabled={busy} className="inline-flex min-h-11 items-center gap-2 bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radio className="h-4 w-4" />}{s ? 'Check again' : 'Scout the scene'}</button>
        {s && <button type="button" onClick={print} className="inline-flex min-h-11 items-center gap-2 border border-foreground px-4 text-sm font-semibold"><Printer className="h-4 w-4" />Print</button>}
      </div>
      {busy && !s && <p className="mt-4 text-sm text-muted-foreground">Reading about 20 venue websites and picking out what to cover… this can take up to a minute.</p>}
      {error && <p role="alert" className="mt-4 text-sm text-destructive">{error}</p>}
      {s && (
        <div ref={docRef} className={`mt-6 bg-[hsl(var(--paper))] p-6 sm:p-8 ${busy ? 'opacity-60' : ''}`}>
          <header className="flex items-start justify-between gap-4 border-b-4 border-double border-foreground pb-3">
            <div className="min-w-0"><div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Assignment ideas · <em>The Village Star-Revue</em></div>
              <h3 className="font-[family-name:var(--font-heading)] text-3xl font-semibold">The Scene</h3>
              <p className="text-sm text-muted-foreground">Underground music, literary, theater &amp; counterculture · Prepared {new Date(s.generated).toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p></div>
            <img src={VSR_LOGO} alt="The Village Star-Revue" crossOrigin="anonymous" className="h-16 w-16 shrink-0 object-contain" />
          </header>
          {s.summary && <p className="mt-4 text-base leading-relaxed">{s.summary}</p>}
          <h4 className="mt-6 text-xs font-bold uppercase tracking-[0.18em]">Coming up ({s.events.length})</h4>
          {s.events.length ? <ul className="mt-1">{s.events.map((e) => <Item key={e.title + e.when + e.venue} e={e} />)}</ul> : <p className="mt-2 text-sm text-muted-foreground">No dated upcoming events were listed on these sites.</p>}
          {s.ongoing.length > 0 && <><h4 className="mt-6 text-xs font-bold uppercase tracking-[0.18em]">Ongoing stories &amp; scene developments ({s.ongoing.length})</h4><ul className="mt-1">{s.ongoing.map((e) => <Item key={e.title + e.venue} e={e} />)}</ul></>}
          <p className="mt-6 border-t border-border pt-2 text-xs text-muted-foreground">Read: {s.read.join(', ')}.{s.failed.length ? ` Could not read: ${s.failed.join(', ')}.` : ''} Suggestions are generated by AI from the venues' own text. Always check the date on the source before assigning.</p>
        </div>
      )}
    </div>
  );
}
